import Media from 'react-media';
import { useTracking } from 'react-tracking';

import {
  Box,
  Button as LegacyButton,
  Grid,
  Text,
  useTheme,
} from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import Carousel from '@components/Carousel';
import { Sentry } from '@/components/Sentry';

import { useCompanyNarratives } from '@/pages/CompanyReport/components/CompanyNarratives/hooks/useCompanyNarratives';
import { Section } from '@/pages/CompanyReport/components/Overview/components/Section';
import { SubSectionHeader } from '@/pages/CompanyReport/components/Overview/components/SubSectionHeader';

import { StyledHeaderGrid } from './styled';
import { StyledEmbla } from '@/components/Carousel/styled';
import {
  NarrativeCardPopulated,
  BaseRouterLinkWithExtraHeight,
  ContentContainerWithHover,
} from '@/components/NarrativeCard';
import { RouterLink } from '@/utilities/redux-first-router.export';
import { ROUTE_PUBLIC_NARRATIVE_CREATE } from '@/constants/routes';
import { getLocationPathname } from '@/redux/location/selectors';
import { useSelector } from 'react-redux';
import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';
import { useFlag } from '@/features/unleash';
import ChevronRightSmallSolid from '@simplywallst/ui-core/icons/ChevronRightSmallSolid';
import { Badge } from '@simplywallst/ui-core/next';
import { useEffect, useRef, useState } from 'react';

const VISIBLE_NARRATIVES = 4;

const BaseExcerpt = ({ children }: { children: string }) => (
  <span className="mt-2 text-xs text-soft no-underline focus:no-underline hover:no-underline line-clamp-2">
    {children}
  </span>
);

const ReadMore = () => {
  return (
    <span className="text-xs text-p-blue no-underline focus:no-underline">
      {' Read more'}
    </span>
  );
};

const ReadMoreExcerpt = ({ children }: { children: string }) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const excerptRef = useRef<HTMLDivElement>(null);

  const checkOverflow = (element: HTMLElement) => {
    const curOverflow = element.style.overflow;

    // Check overflow
    element.style.overflow = 'hidden';
    const isOverflown =
      element.clientWidth < element.scrollWidth ||
      element.clientHeight < element.scrollHeight;
    element.style.overflow = curOverflow;

    return isOverflown;
  };

  useEffect(() => {
    if (excerptRef.current) {
      setIsOverflowing(checkOverflow(excerptRef.current));
    }
  }, [children]);

  if (!isOverflowing) {
    return (
      <div className="mt-2 relative">
        <span
          ref={excerptRef}
          className="text-xs text-soft no-underline focus:no-underline hover:no-underline line-clamp-2"
        >
          {children}
          <ReadMore />
        </span>
      </div>
    );
  }

  return (
    <div className="mt-2 relative">
      <span
        ref={excerptRef}
        className="text-xs text-soft no-underline focus:no-underline hover:no-underline"
      >
        {children.slice(0, children.length - 23) + '...'}
        <ReadMore />
      </span>
    </div>
  );
};

const ViewNarrativeExcerpt = ({ children }: { children: string }) => {
  return (
    <div className="mt-2">
      <div className="text-xs no-underline focus:no-underline hover:no-underline overflow-ellipsis line-clamp-2 bg-gradient-to-b from-p-white-150 to-transparent bg-clip-text text-transparent">
        {children}
      </div>
      <span className="flex justify-center items-center">
        <Badge className="flex items-center py-[0px] px-4 text-p-white absolute bottom-[19px]">
          View narrative
          <ChevronRightSmallSolid className="fill-soft" />
        </Badge>
      </span>
    </div>
  );
};

const Excerpt = ({ children }: { children: string }) => {
  const flag = useFlag({ name: 'narrative-content-card-variants' });

  if (flag.variant.name === 'readmore') {
    return <ReadMoreExcerpt>{children}</ReadMoreExcerpt>;
  }

  if (flag.variant.name === 'basicexcerpt') {
    return <BaseExcerpt>{children}</BaseExcerpt>;
  }

  if (flag.variant.name === 'viewnarrative') {
    return <ViewNarrativeExcerpt>{children}</ViewNarrativeExcerpt>;
  }

  // Should be default variant or control variant
  return <></>;
};

export const CompanyNarratives = ({
  canonicalUrl,
}: {
  canonicalUrl: string;
}) => {
  const theme = useTheme();
  const { isMobile, isDesktop } = useUserAgent();
  const { data, status } = useCompanyNarratives({ canonicalUrl });
  const narratives = data?.NarrativesByCompanyCanonical;
  const pathname = useSelector(getLocationPathname);
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'narrative',
  });

  if (status === 'error' || status === 'pending') {
    return null;
  }

  if (typeof narratives === 'undefined' || narratives.length === 0) {
    return null;
  }

  const handleOnClick = (narrative: (typeof narratives)[number]) => {
    const { id, company } = narrative;
    return trackEvent({
      action: 'click',
      modifier: 'view',
      narrativeId: id,
      companyId: company?.id,
      uniqueSymbol: company?.tickerSymbol,
    });
  };

  const hasMoreThanOneNarrative = narratives.length > 1;
  const hasOneNarrative = narratives.length === 1;
  const companyId = narratives?.[0]?.company?.id;

  return (
    <Track>
      <Section data-cy-id="company-narratives">
        <StyledHeaderGrid
          as="header"
          templateColumns="1fr max-content"
          gap="8px"
        >
          <SubSectionHeader data-cy-id="company-narratives-heading">
            Community Narratives
          </SubSectionHeader>
          {companyId && (
            <LegacyButton
              as={RouterLink}
              styleType="loud"
              to={{
                type: ROUTE_PUBLIC_NARRATIVE_CREATE,
                payload: {
                  query: { company: companyId, returnPath: pathname },
                },
              }}
              alignIcon="left"
            >
              <PlusSolid />
              Create a narrative
            </LegacyButton>
          )}
        </StyledHeaderGrid>
        <Box marginBottom="x2">
          <Text typography="xSmall" data-cy-id="company-narratives-by-line">
            Narratives bring a range of perspectives from our community.
          </Text>
        </Box>
        <Sentry
          sentryCapture={{ team: 'narwhals', extra: { canonicalUrl } }}
          globalDisabled
        >
          <Media
            queries={{
              mobile: { maxWidth: theme.smMaxRaw },
              desktop: { minWidth: theme.lgMinRaw },
            }}
            defaultMatches={{
              mobile: isMobile,
              desktop: isDesktop,
            }}
          >
            {(matches) => {
              if (matches.mobile) {
                return (
                  <StyledEmbla>
                    <Carousel
                      align="center"
                      containScroll="trimSnaps"
                      showArrows={hasMoreThanOneNarrative}
                      showGradient={hasMoreThanOneNarrative}
                      totalSlides={VISIBLE_NARRATIVES}
                    >
                      {hasOneNarrative ? (
                        <Grid width="100%">
                          <div>
                            {narratives.map((narrative) => (
                              <NarrativeCardPopulated
                                key={narrative.id}
                                narrative={narrative}
                                excerptNode={
                                  <Excerpt>{narrative.excerpt}</Excerpt>
                                }
                                onClick={() => handleOnClick(narrative)}
                                ContentContainer={ContentContainerWithHover}
                                CustomRouterLink={BaseRouterLinkWithExtraHeight}
                              />
                            ))}
                          </div>
                        </Grid>
                      ) : (
                        <Grid
                          gap="8px"
                          templateColumns={`repeat(${VISIBLE_NARRATIVES}, 317px)`}
                          width="100%"
                        >
                          {narratives
                            .slice(0, VISIBLE_NARRATIVES)
                            .map((narrative) => (
                              <div key={narrative.id}>
                                <NarrativeCardPopulated
                                  narrative={narrative}
                                  excerptNode={
                                    <Excerpt>{narrative.excerpt}</Excerpt>
                                  }
                                  ContentContainer={ContentContainerWithHover}
                                  onClick={() => handleOnClick(narrative)}
                                  CustomRouterLink={
                                    BaseRouterLinkWithExtraHeight
                                  }
                                />
                              </div>
                            ))}
                        </Grid>
                      )}
                    </Carousel>
                  </StyledEmbla>
                );
              }

              return (
                <Grid gap={`${theme.x2}px`} templateColumns="repeat(2, 1fr)">
                  {narratives.slice(0, VISIBLE_NARRATIVES).map((narrative) => (
                    <NarrativeCardPopulated
                      key={narrative.id}
                      narrative={narrative}
                      onClick={() => handleOnClick(narrative)}
                      excerptNode={<Excerpt>{narrative.excerpt}</Excerpt>}
                      ContentContainer={ContentContainerWithHover}
                      CustomRouterLink={BaseRouterLinkWithExtraHeight}
                    />
                  ))}
                </Grid>
              );
            }}
          </Media>
          {narratives.length > VISIBLE_NARRATIVES && (
            <div className="grid grid-cols-1 mt-x2">
              <LegacyButton
                styleType="loudLarge"
                as={RouterLink}
                to={canonicalUrl.replace('/stocks', '/community/narratives')}
                className="text-center"
              >
                View all narratives
              </LegacyButton>
            </div>
          )}
        </Sentry>
      </Section>
    </Track>
  );
};
